import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Plus, Trash2, Camera, Loader2, Pencil, X, Send, User } from "lucide-react";

const empty = { cliente: "", note: "", foto_file_ids: [] };

export default function Consegne({ embedded = false }) {
  const { text } = useSettings();
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.is_admin || !!user.permissions_effective?.consegne?.manage;
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/consegne"); setItems(data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const uploadPhotos = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, foto_file_ids: [...f.foto_file_ids, data.file_id] }));
      }
      toast.success("Foto caricate");
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const removePhoto = (id) => setForm((f) => ({ ...f, foto_file_ids: f.foto_file_ids.filter((x) => x !== id) }));

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ cliente: c.cliente || "", note: c.note || "", foto_file_ids: c.foto_file_ids || [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditId(null); setForm(empty); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    try {
      if (editId) { await api.put(`/consegne/${editId}`, form); toast.success("Consegna aggiornata"); }
      else { await api.post("/consegne", form); toast.success("Consegna registrata"); }
      cancelEdit(); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/consegne/${id}`); toast.success("Eliminata"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary";

  return (
    <div className="animate-fade-up">
      {!embedded && (
        <>
          <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("consegne_title")}</h1>
          <p className="text-slate-400 mb-6">{text("consegne_subtitle")}</p>
        </>
      )}

      {canManage && (
        <form onSubmit={submit} data-testid="consegna-form" className="bg-slate-900/50 border border-slate-800 rounded-md p-5 mb-8 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input data-testid="consegna-cliente" placeholder="Nome cliente" value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} className={input} />
          <label data-testid="consegna-foto-label" className={`${input} flex items-center gap-2 cursor-pointer ${form.foto_file_ids.length ? "text-emerald-400 border-emerald-500/40" : "text-slate-400"}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <span className="truncate">{form.foto_file_ids.length ? `${form.foto_file_ids.length} foto allegate ✓` : "Allega foto (una o più)"}</span>
            <input data-testid="consegna-foto-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} />
          </label>
          <textarea data-testid="consegna-note" placeholder="Note / info consegna (indirizzo, orario, chi ha ricevuto…)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className={`${input} md:col-span-2 resize-none`} />
          {form.foto_file_ids.length > 0 && (
            <div className="md:col-span-2 flex flex-wrap gap-2">
              {form.foto_file_ids.map((id) => (
                <div key={id} className="relative">
                  <img src={fileUrl(id)} alt="foto" className="w-16 h-16 rounded-md object-cover border border-slate-700" />
                  <button type="button" onClick={() => removePhoto(id)} className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5 text-white"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          <button data-testid="consegna-submit" type="submit" className="bg-primary text-slate-950 font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
            {editId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editId ? "Aggiorna consegna" : "Registra consegna"}
          </button>
          {editId && <button type="button" data-testid="consegna-cancel" onClick={cancelEdit} className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="w-3 h-3" /> Annulla</button>}
        </form>
      )}

      <div className="space-y-3" data-testid="consegne-list">
        {items.length === 0 && <p className="text-slate-500 text-sm">Nessuna consegna registrata.</p>}
        {items.map((c) => (
          <div key={c.id} data-testid={`consegna-${c.id}`} className="bg-slate-900/50 border border-slate-800 rounded-md p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-cyan-500/15 flex items-center justify-center shrink-0"><Send className="w-5 h-5 text-cyan-300" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-100 flex items-center gap-1"><User className="w-3.5 h-3.5 text-slate-400" />{c.cliente}</span>
                  <span className="text-[11px] text-slate-500">{new Date(c.data || c.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                {c.note && <p className="text-sm text-slate-400 mt-1">{c.note}</p>}
                <p className="text-[11px] text-slate-500 mt-1">Autista: {c.created_by_name || "—"}</p>
                {c.foto_file_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {c.foto_file_ids.map((id) => (
                      <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(id)} alt="consegna" className="w-16 h-16 rounded-md object-cover border border-slate-700 hover:border-primary transition-colors" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`edit-consegna-${c.id}`} onClick={() => startEdit(c)} className="p-2 text-slate-500 hover:text-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-consegna-${c.id}`} onClick={() => remove(c.id)} className="p-2 text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
